import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  CircularProgress, 
  Button, 
  Tab, 
  Tabs,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Divider,
  Avatar,
  Container
} from '@mui/material';
import '../styles/CandidateDashboard.css';
import { 
  Person, 
  Work, 
  School, 
  LocationOn, 
  Phone, 
  Email, 
  DateRange,
  CheckCircle,
  ErrorOutline,
  ArrowUpward,
  Description
} from '@mui/icons-material';
import axios from 'axios';
import ProfileUpdate from '../components/dashboard/ProfileUpdate';
import ApplicationStatus from '../components/dashboard/ApplicationStatus';
import ProfileCompletionStatus from '../components/dashboard/ProfileCompletionStatus';
import DashboardSummary from '../components/dashboard/DashboardSummary';
import { useAuth } from '../../AuthContext'; 

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `dashboard-tab-${index}`,
    'aria-controls': `dashboard-tabpanel-${index}`,
  };
}

const CandidateDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [candidateData, setCandidateData] = useState(null);
  const [applicationData, setApplicationData] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [profileCompletion, setProfileCompletion] = useState({
    overall: 0,
    personal: 0,
    education: 0,
    experience: 0,
    skills: 0
  });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Fetch candidate data using the user's email
  useEffect(() => {
    const fetchCandidateData = async () => {
      if (!user || !user.email) return;
      
      try {
        setLoading(true);
        const response = await axios.get(`/api/candidates/email/${user.email}`);
        setCandidateData(response.data);
        
        // Calculate profile completion
        calculateProfileCompletion(response.data);
        
        // Fetch application status
        const applicationsResponse = await axios.get(`/api/associations/candidate/${response.data._id}`);
        setApplicationData(applicationsResponse.data);
      } catch (error) {
        console.error('Error fetching candidate data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCandidateData();
  }, [user]);

  const calculateProfileCompletion = (data) => {
    if (!data) return;

    // Define required fields for each section
    const personalFields = ['name', 'email', 'phone', 'currentLocation', 'gender', 'DOB'];
    const educationFields = ['education'];
    const experienceFields = ['experience', 'skills', 'currentCompany', 'currentRole'];

    // Calculate completion percentages
    let personalComplete = 0;
    let educationComplete = 0;
    let experienceComplete = 0;
    let skillsComplete = 0;

    // Personal info completion
    personalComplete = personalFields.reduce((sum, field) => 
      sum + (data[field] && data[field] !== '' ? 1 : 0), 0) / personalFields.length * 100;

    // Education completion
    if (data.education && data.education.length > 0) {
      educationComplete = 100; // Consider education as complete if at least one entry exists
    }

    // Experience completion
    if (data.experience && data.experience.length > 0) {
      experienceComplete = 100; // Consider experience as complete if at least one entry exists
    }

    // Skills completion
    if (data.skills && data.skills.length > 0) {
      skillsComplete = Math.min(100, data.skills.length * 10); // 10% per skill, up to 100%
    }

    // Calculate overall completion
    const overall = (personalComplete + educationComplete + experienceComplete + skillsComplete) / 4;

    setProfileCompletion({
      overall: Math.round(overall),
      personal: Math.round(personalComplete),
      education: Math.round(educationComplete),
      experience: Math.round(experienceComplete),
      skills: Math.round(skillsComplete)
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" className="dashboard-container fade-in">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }} className="section-header">
          Candidate Dashboard
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Welcome back, {candidateData?.name || user?.name || 'Candidate'}!
        </Typography>
        
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {/* Summary Section */}
          <Grid item xs={12}>
            <DashboardSummary 
              candidateData={candidateData} 
              applicationData={applicationData}
              profileCompletion={profileCompletion}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Paper sx={{ borderRadius: 2 }} className="dashboard-card">
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs 
                  value={tabValue} 
                  onChange={handleTabChange} 
                  variant="fullWidth"
                  indicatorColor="primary"
                  textColor="primary"
                >
                  <Tab label="Profile" icon={<Person />} iconPosition="start" {...a11yProps(0)} />
                  <Tab label="Application Status" icon={<Work />} iconPosition="start" {...a11yProps(1)} />
                  <Tab label="Profile Completion" icon={<CheckCircle />} iconPosition="start" {...a11yProps(2)} />
                </Tabs>
              </Box>

              <TabPanel value={tabValue} index={0}>
                <ProfileUpdate 
                  candidateData={candidateData} 
                  setCandidateData={setCandidateData} 
                />
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <ApplicationStatus applicationData={applicationData} />
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <ProfileCompletionStatus profileCompletion={profileCompletion} candidateData={candidateData} />
              </TabPanel>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default CandidateDashboard;
